import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Card,
  Chip,
  DataTable,
  Field,
  Input,
  Modal,
  PageHead,
  Pager,
  SearchInput,
  StateBlock,
  Textarea,
  useToast,
  type Column,
} from '@w7/ui'
import { ApiError } from '@/api/client'
import { count, EMPTY, lll } from '@/lib/format'
import type { BlockedIpRow } from './blockedIpTypes'
import { useBlockedIps } from './useBlockedIps'
import { useDeleteBlockedIp, useSaveBlockedIp } from './useBlockedIpMutations'

/**
 * Blocked IP — Winner All Logs (Master only).
 *
 * Filters/columns match winner-golive blocked-ip.html.
 * List: Agent-API `getBlockedIps`.
 * Add / Delete: Agent-API `BlockedIp/save` + `deleteBlockedIp`.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function BlockedIpPage() {
  const toast = useToast()
  const [page, setPage] = useState(1)
  const [ip, setIp] = useState('')
  const [player, setPlayer] = useState('')
  const [applied, setApplied] = useState({ ip: '', player: '' })
  const [addOpen, setAddOpen] = useState(false)
  const [newIp, setNewIp] = useState('')
  const [newComment, setNewComment] = useState('')
  const [pendingDelete, setPendingDelete] = useState<BlockedIpRow | null>(null)

  const { data, isLoading } = useBlockedIps({
    page,
    pageSize: PAGE_SIZE,
    ip: applied.ip,
    player: applied.player,
  })
  const saveMutation = useSaveBlockedIp()
  const deleteMutation = useDeleteBlockedIp()

  const columns = useMemo<Column<BlockedIpRow>[]>(
    () => [
      {
        id: 'index',
        header: '#',
        align: 'right',
        numeric: true,
        cell: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (row) => row.ip || EMPTY,
      },
      {
        id: 'comment',
        header: 'Comment',
        cell: (row) => row.comment || EMPTY,
      },
      {
        id: 'createdAt',
        header: 'Date',
        cell: (row) => (row.createdAt ? lll(row.createdAt) : EMPTY),
      },
      {
        id: 'players',
        header: 'Players',
        cell: (row) => (
          <span className="w7-row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {row.players.map((p) => (
              <Chip key={`${row.id}-${p.id}`} size="sm" tone="muted">
                {p.username || p.id}
              </Chip>
            ))}
          </span>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        cell: (row) => (
          <Button
            variant="danger"
            size="sm"
            disabled={!row.id || deleteMutation.isPending}
            onClick={() => setPendingDelete(row)}
          >
            Delete
          </Button>
        ),
      },
    ],
    [page, deleteMutation.isPending],
  )

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  const search = () => {
    setPage(1)
    setApplied({ ip: ip.trim(), player: player.trim() })
  }

  const resetAddForm = () => {
    setAddOpen(false)
    setNewIp('')
    setNewComment('')
  }

  const closeAdd = () => {
    if (saveMutation.isPending) return
    resetAddForm()
  }

  const onSave = async () => {
    try {
      await saveMutation.mutateAsync({
        IP: newIp,
        comment: newComment,
      })
      toast.push({
        tone: 'success',
        message: 'IP Blocked successfully.',
        detail: 'Blocked',
      })
      resetAddForm()
    } catch (err) {
      toast.push({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Error in IP Blocked',
        detail: 'Blocked',
      })
    }
  }

  const onConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteMutation.mutateAsync({
        id: pendingDelete.id,
        ip: pendingDelete.ip,
        comment: pendingDelete.comment,
      })
      toast.push({
        tone: 'success',
        message: 'IP deleted successfully.',
      })
      setPendingDelete(null)
    } catch (err) {
      const detail =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : undefined
      toast.push({
        tone: 'error',
        message: 'There was an error while deleting IP',
        detail: detail ?? 'IP deleting error',
      })
    }
  }

  const canSave = newIp.trim().length > 0 && !saveMutation.isPending

  return (
    <>
      <PageHead
        title="Blocked IP"
        subtitle="Blocked IP addresses and players seen on them — Master All Logs from Winner"
      />

      <Card
        title="Filters"
        subtitle="Search by IP address or player name"
        actions={
          <span className="w7-row" style={{ gap: 8 }}>
            <Button variant="primary" onClick={search}>
              Search
            </Button>
            <Button variant="secondary" onClick={() => setAddOpen(true)}>
              Add New IP
            </Button>
          </span>
        }
      >
        <div className="w7-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="IP"
              value={ip}
              onChange={setIp}
              placeholder="Enter IP address…"
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="Player"
              value={player}
              onChange={setPlayer}
              placeholder="Enter player name…"
            />
          </div>
        </div>
      </Card>

      <Card
        flush
        title="Blocked Ip List"
        subtitle={isLoading ? 'Loading' : `${count(total)} in this list`}
        footer={
          <Pager
            page={data?.page ?? page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Blocked IP list"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={6}
          empty={
            <StateBlock icon="sliders" title="No data found">
              No blocked IPs match these filters.
            </StateBlock>
          }
        />
      </Card>

      <Modal
        open={addOpen}
        onClose={closeAdd}
        title="Create Blocked IP"
        subtitle="Blocks this IP address for the console tree"
        closeDisabled={saveMutation.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button variant="secondary" onClick={closeAdd} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!canSave}
              busy={saveMutation.isPending}
              onClick={() => void onSave()}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
          <Field label="IP" required>
            {({ id }) => (
              <Input
                id={id}
                value={newIp}
                onChange={(event) => setNewIp(event.target.value)}
                placeholder="Enter IP"
                disabled={saveMutation.isPending}
              />
            )}
          </Field>
          <Field label="Comment">
            {({ id }) => (
              <Textarea
                id={id}
                value={newComment}
                onChange={(event) => setNewComment(event.target.value)}
                placeholder="Enter Comments"
                disabled={saveMutation.isPending}
              />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={pendingDelete !== null}
        onClose={() => {
          if (!deleteMutation.isPending) setPendingDelete(null)
        }}
        title="Delete IP"
        subtitle={
          pendingDelete?.ip
            ? 'Are you sure you want to Delete IP ' + pendingDelete.ip + '?'
            : 'Are you sure you want to Delete IP?'
        }
        closeDisabled={deleteMutation.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button
              variant="secondary"
              disabled={deleteMutation.isPending}
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              busy={deleteMutation.isPending}
              disabled={deleteMutation.isPending}
              onClick={() => void onConfirmDelete()}
            >
              Delete
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          This removes the IP block from the list. Login history for that IP is unchanged.
        </p>
      </Modal>
    </>
  )
}
